import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { earningsApi, SettlementDetail, TaxInvoiceUrlResponse } from '../services/earningsApi';

const aed = (n: number | null | undefined) =>
  `AED ${(n ?? 0).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const shortDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function PartnerSettlementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState<SettlementDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Spec 051 AC-22 — commission tax invoice state. null = availability unknown
  // (still loading or the check failed) → no button rendered.
  const [taxInvoice, setTaxInvoice] = useState<TaxInvoiceUrlResponse | null>(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const load = useCallback(async () => {
    if (!token || !id) return;
    setLoading(true);
    try {
      setS(await earningsApi.getSettlement(id, token));
    } catch {
      setError('This settlement could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [token, id]);

  useEffect(() => { load(); }, [load]);

  // Availability check is separate and non-fatal: a failure just hides the button.
  useEffect(() => {
    if (!token || !id) return;
    earningsApi.getTaxInvoiceUrl(id, token)
      .then(setTaxInvoice)
      .catch(() => setTaxInvoice(null));
  }, [token, id]);

  const downloadInvoice = async () => {
    if (!token || !id) return;
    setDownloadingInvoice(true);
    try {
      // Re-request at click time so the presigned URL is always fresh.
      const res = await earningsApi.getTaxInvoiceUrl(id, token);
      setTaxInvoice(res);
      if (res.status === 'AVAILABLE' && res.downloadUrl) {
        window.open(res.downloadUrl, '_blank', 'noopener');
      } else {
        alert('That document is not available yet.');
      }
    } catch {
      alert('That document is not available yet.');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  if (loading) return <div className="earnings-page"><div className="earnings-spinner" /></div>;
  if (error || !s) return <div className="earnings-page"><p className="earnings-error">{error}</p></div>;

  // Vertical calculation stack — reads top to bottom to the net payout (Spec 067: the rows add up):
  // gross − commission − VAT − refunds − Tabby fees − their VAT − service fee recharges − their VAT + adjustments + balance in, and a
  // shortfall (carriedBalanceOut ≤ 0) is carried to the next settlement, so the payout never goes below 0.
  const tabbyFees = s.gatewayFeeDeductions ?? 0;
  const tabbyFeeVat = s.gatewayFeeVat ?? 0;
  // Spec 068 V16 — present only after a GARAGE-fault refund of Garagat's service fee.
  const serviceFeeRecharges = s.serviceFeeDeductions ?? 0;
  const serviceFeeRechargesVat = s.serviceFeeDeductionsVat ?? 0;
  // #1054 — the per-order recharge column, on the same rule as the recharge rows: hidden when the settlement has none.
  const showServiceFeeRecharges = serviceFeeRecharges > 0 || serviceFeeRechargesVat > 0;
  const carriedIn = s.carriedBalanceIn ?? 0;
  const carriedOut = s.carriedBalanceOut ?? s.carriedBalance ?? 0;
  const rows: { label: string; value: number; kind?: 'deduct' | 'total' }[] = [
    { label: 'Gross order value', value: s.grossRevenue },
    { label: 'Garagat commission', value: -s.commissionTotal, kind: 'deduct' },
    { label: 'VAT on commission (5%)', value: -s.vatOnCommission, kind: 'deduct' },
    { label: 'Refund adjustments', value: -s.refundDeductions, kind: 'deduct' },
    ...(tabbyFees !== 0 || tabbyFeeVat !== 0
      ? [
        { label: 'Tabby fees', value: -tabbyFees, kind: 'deduct' as const },
        { label: 'VAT on Tabby fees', value: -tabbyFeeVat, kind: 'deduct' as const },
      ]
      : []),
    ...(showServiceFeeRecharges
      ? [
        { label: 'Service fee recharges', value: -serviceFeeRecharges, kind: 'deduct' as const },
        { label: 'VAT on service fee recharges', value: -serviceFeeRechargesVat, kind: 'deduct' as const },
      ]
      : []),
    { label: 'Adjustments', value: s.adjustmentsTotal ?? 0 },
    ...(carriedIn !== 0 ? [{ label: 'Balance from previous settlement', value: carriedIn }] : []),
    // Shown as the amount added back so the payout stops at 0; it is deducted from the next payout.
    { label: 'Balance carried forward', value: carriedOut === 0 ? 0 : -carriedOut },
    { label: 'Net payout', value: s.netPayable, kind: 'total' },
  ];

  return (
    <div className="earnings-page">
      <button className="earnings-back" onClick={() => navigate('/earnings')}>← Earnings</button>

      <div className="earnings-detail-head">
        <div>
          <h1 className="earnings-title mono">{s.settlementNumber}</h1>
          <p className="earnings-detail-period">
            {shortDate(s.periodStart)} – {shortDate(s.periodEnd)} · {s.orderCount} orders
          </p>
        </div>
        <div className="earnings-detail-net">
          <span className="earnings-card-label">Net payout</span>
          <span className="earnings-detail-net-value">{aed(s.netPayable)}</span>
          {s.settledAt && <span className="earnings-card-sub">Paid {shortDate(s.settledAt)}</span>}
        </div>
      </div>

      <div className="earnings-detail-grid">
        {/* Calculation stack */}
        <div className="earnings-panel">
          <h2 className="earnings-panel-title">How this was calculated</h2>
          <div className="earnings-stack">
            {rows.map((r) => (
              <div key={r.label} className={`earnings-stack-row ${r.kind ?? ''}`}>
                <span>{r.label}</span>
                <span className="num">{r.value < 0 ? `- ${aed(Math.abs(r.value))}` : aed(r.value)}</span>
              </div>
            ))}
          </div>
          {/* Statement PDF button removed — its endpoint is intentionally unbuilt
              (deferred from Spec 049). Commission tax invoice per Spec 051 AC-22. */}
          {taxInvoice && taxInvoice.status !== 'NOT_AVAILABLE' && (
            <div className="earnings-docs">
              {taxInvoice.status === 'AVAILABLE' ? (
                <button
                  className="earnings-btn-outline"
                  onClick={downloadInvoice}
                  disabled={downloadingInvoice}
                >
                  {downloadingInvoice ? 'Preparing…' : 'Commission tax invoice'}
                  {taxInvoice.invoiceNumber ? ` (${taxInvoice.invoiceNumber})` : ''}
                </button>
              ) : (
                <button className="earnings-btn-outline" disabled>
                  Commission tax invoice — Generating…
                </button>
              )}
            </div>
          )}
        </div>

        {/* Order lines */}
        <div className="earnings-panel">
          <h2 className="earnings-panel-title">Orders in this settlement</h2>
          <div className="earnings-table-wrap">
            <table className="earnings-table compact">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th className="num">Gross</th>
                  <th className="num">Commission</th>
                  {showServiceFeeRecharges && <th className="num">Service fee recharge</th>}
                  <th className="num">Your net</th>
                </tr>
              </thead>
              <tbody>
                {s.lineItems.map((li) => (
                  <tr key={li.orderId} className={li.isRefunded ? 'refunded' : ''}>
                    <td>
                      <Link to={`/orders/${li.orderId}`} className="earnings-order-link">
                        {li.orderNumber ?? li.orderId.slice(0, 8)}
                      </Link>
                      {li.isRefunded && <span className="earnings-refund-tag">Refunded</span>}
                    </td>
                    <td>{li.customerName ?? '—'}</td>
                    <td className="num">{aed(li.grossAmount)}</td>
                    <td className="num deduct">
                      - {aed(li.commissionAmount)}
                      {(li.reversedCommission > 0 || li.reversedVat > 0) && (
                        <span className="earnings-reversed">
                          − reversed {aed(li.reversedCommission)} / {aed(li.reversedVat)} VAT
                        </span>
                      )}
                    </td>
                    {showServiceFeeRecharges && (
                      <td className="num deduct recharge">
                        {(li.serviceFeeDeduction ?? 0) > 0 ? `- ${aed(li.serviceFeeDeduction as number)}` : '—'}
                        {(li.serviceFeeDeductionVat ?? 0) > 0 && (
                          <span className="earnings-reversed">VAT - {aed(li.serviceFeeDeductionVat as number)}</span>
                        )}
                      </td>
                    )}
                    <td className="num strong">{aed(li.netAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
